/**
 * BundleModal (hardened)
 * Goals:
 *  - Add main + bundle children reliably
 *  - Remove existing bundle lines reliably (by line-item key via cart/change.js)
 *  - Verify cart state after add; re-add missing lines if needed
 *  - Prevent double-submit and stop other click/submit handlers
 *
 * Notes:
 *  - Assumes your theme marks existing bundle lines with [data-bundle-item] and data-line-item-key
 *  - Assumes selected product cards contain input[name="id"] with the child variant id
 *  - Assumes kit/free ids exist in DOM as numeric text in #kit-1-id, #kit-2-id, #free-id (or omit if not present)
 */

class BundleModal {
  constructor() {
    // HHP Customisation START
    this.trackedProductIds = [];
    // HHP Customisation END

    this.modal = document.getElementById('bundleModal');
    this.overlay = document.querySelector('.bundle-modal-overlay');
    this.title = this.modal?.querySelector('#bundleModalTitle');
    this.progress = this.modal?.querySelector('.bundle-progress');
    this.formProgress = document.querySelector('.bundle-form-progress-bar .bundle-progress');
    this.toggleButtons = this.modal?.querySelectorAll('.bundle-product-toggle') || [];
    this.closeButton = this.modal?.querySelector('[data-bundle-close]');

    this.selectedProducts = [];
    this.MAX_SELECTIONS = 6;

    // HHP Customisation START
    this.mainForm = document.querySelector('form[action^="/cart/add"]');
    // HHP Customisation END

    this.addToCartButton = document.querySelector('[data-add-to-cart-bundle]');
    this.addToBundleButton = document.querySelector('[data-add-to-bundle]');

    this.originalButtonText = this.addToCartButton
      ? this.addToCartButton.querySelector('[data-add-to-cart-text]')?.textContent || ''
      : '';
    this.originalBundleButtonText = this.addToBundleButton
      ? this.addToBundleButton.querySelector('[data-add-to-bundle-text]')?.textContent || ''
      : '';

    this.bundleSlots = document.querySelectorAll('.bundle-product-slot');
    this.selectedCounter = document.querySelector('.bundle-product-toggle-text-count span');
    this.mainCountTextElement = document.querySelector('.bundle-product-toggle-text-count');
    this.modalCountTextElement = document.querySelector('.bundle-modal-header .bundle-product-toggle-text-count');

    this.currentSlot = null;

    // 슬롯별 선택된 제품 추적
    this.slotSelections = {};

    // Submission guard
    this.isSubmitting = false;

    this.init();
    this.restoreSelectedState();

    // variant 변경 이벤트 리스너 수정
    document.querySelectorAll('[name="options[Option]"]').forEach((radio) => {
      radio.addEventListener('change', () => {
        this.resetAllSelections();
        this.updateMaxSelections();
        this.updateSlotVisibility();
      });
    });

    this.updateMaxSelections();
    this.updateSlotVisibility();

    // 제거 버튼 이벤트 리스너 추가
    this.bundleSlots.forEach((slot) => {
      const removeButton = slot.querySelector('.bundle-slot-remove');
      if (removeButton) {
        removeButton.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.removeSelection(parseInt(slot.dataset.slot, 10));
        });
      }
    });

    // Swipers (keep your existing behavior)
    this.headerSlider = new Swiper('.bundle-modal-header .swiper', {
      slidesPerView: 'auto',
      spaceBetween: 10,
      watchSlidesProgress: true
    });

    this.collectionsSlider = new Swiper('.bundle-collections .swiper', {
      slidesPerView: 'auto',
      spaceBetween: 10,
      watchSlidesProgress: true,
      slidesOffsetBefore: 15,
      slidesOffsetAfter: 15
    });

    // Collection links
    this.modal?.querySelectorAll('.bundle-collections a').forEach((link) => {
      link.addEventListener('click', (e) => {
        e.preventDefault();
        this.switchCollection(link.dataset.collectionId);
      });
    });

    this.initializeFirstCollection();

    // Overlay click closes modal
    this.overlay?.addEventListener('click', (e) => {
      if (e.target === this.overlay && e.currentTarget === this.overlay) {
        this.close();
      }
    });
  }

  // ---------------------------
  // Low-level helpers
  // ---------------------------
  safeParseInt(value) {
    const n = parseInt(String(value ?? '').trim(), 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  getText(selector) {
    const el = document.querySelector(selector);
    return el ? (el.textContent || '').trim() : '';
  }

  getHTML(selector) {
    const el = document.querySelector(selector);
    return el ? (el.innerHTML || '').trim() : '';
  }

  getSrc(selector) {
    const el = document.querySelector(selector);
    return el && el.src ? el.src : '';
  }

  normalizeImageUrl(url) {
    if (!url) return '';
    return url.replace(/_(\d+x\d+|200x_small)\.(jpg|jpeg|png|gif|webp)/i, '.$2');
  }

  setLoading(isLoading) {
    const btn = this.addToCartButton || this.mainForm?.querySelector('[data-add-to-cart-bundle]');
    if (!btn) return;
    btn.classList.toggle('btn--loading', isLoading);
    btn.disabled = !!isLoading;
  }

  async shopifyPost(path, payload) {
    const res = await fetch(window.Shopify.routes.root + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });

    let data = null;
    try {
      data = await res.json();
    } catch (_) {}

    // 🔎 Keep this while diagnosing; remove later if noisy
    console.log(`[Shopify] POST ${path} status=${res.status}`, { payload, data });

    if (!res.ok) {
      const msg = data?.description || data?.message || `Request failed: ${path} (${res.status})`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  async getCart() {
    const res = await fetch(window.Shopify.routes.root + 'cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });
    const cart = await res.json();
    console.log('[Shopify] GET cart.js', cart);
    return cart;
  }

  countVariantInCart(cart, variantId, bundleId = null) {
    let total = 0;
    for (const line of cart.items || []) {
      if (line.id === variantId) {
        if (!bundleId) {
          total += line.quantity;
        } else {
          const props = line.properties || {};
          if (String(props._bundle_id || '') === String(bundleId)) total += line.quantity;
        }
      }
    }
    return total;
  }

  // ---------------------------
  // Init / bindings
  // ---------------------------
  init() {
    this.toggleButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        // Prevent other handlers from treating this as "add" etc.
        e.preventDefault();
        e.stopPropagation();
        this.toggleProduct(button);
      });
    });

    this.closeButton?.addEventListener('click', (e) => {
      e.preventDefault();
      this.close();
    });

    // HARDEN: handle both click AND form submit; stop other handlers
    if (this.addToCartButton) {
      this.addToCartButton.addEventListener(
        'click',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleSubmit();
        },
        true // capture
      );
    }

    if (this.mainForm) {
      this.mainForm.addEventListener(
        'submit',
        (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          this.handleSubmit();
        },
        true // capture
      );
    }

    if (this.addToBundleButton) {
      this.addToBundleButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.open();
      });
    }

    // Cache variant IDs on cards
    this.modal?.querySelectorAll('.bundle-product-card').forEach((card) => {
      const variantInput = card.querySelector('input[name="id"]');
      if (variantInput) card.dataset.variantId = variantInput.value;
    });
  }

  restoreSelectedState() {
    this.selectedProducts.forEach((productId) => {
      const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
      if (productCard) productCard.classList.add('selected');
    });
    this.updateProgress();
    this.updateAddToCartButton();
  }

  updateAddToCartButton() {
    if (this.addToCartButton) {
      const textElement = this.addToCartButton.querySelector('[data-add-to-cart-text]');
      if (textElement) textElement.textContent = this.originalButtonText;
    }
    if (this.addToBundleButton) {
      const textElement = this.addToBundleButton.querySelector('[data-add-to-bundle-text]');
      if (textElement) textElement.textContent = `Choose your Design`;
    }
  }

  // ---------------------------
  // Remove existing bundles (correct by line-item key)
  // ---------------------------
  async removeExistingBundlesByKey() {
    const existingBundles = document.querySelectorAll('[data-bundle-item]');
    if (!existingBundles.length) return;

    const keys = [];
    existingBundles.forEach((bundle) => {
      const key = bundle.getAttribute('data-line-item-key');
      if (key) keys.push(key);
    });

    for (const key of keys) {
      await this.shopifyPost('cart/change.js', { id: key, quantity: 0 });
    }
  }

  // ---------------------------
  // Submit flow: add + verify + repair
  // ---------------------------
  async handleSubmit() {
    if (this.isSubmitting) return;
    if (!this.mainForm) {
      console.error('mainForm not found');
      return;
    }

    this.isSubmitting = true;
    this.setLoading(true);

    try {
      const formData = new FormData(this.mainForm);
      const mainVariantId = this.safeParseInt(formData.get('id'));
      const quantity = this.safeParseInt(formData.get('quantity')) || 1;

      if (!mainVariantId) throw new Error('Main variant id missing/invalid');

      // Remove old bundle lines first (by key)
      await this.removeExistingBundlesByKey();

      // Add new bundle and verify
      await this.addNewBundleProductsVerified(mainVariantId, quantity);

      // Reset UI state after success
      this.resetAllSelections();

      // Redirect only when confirmed
      window.location.href = '/cart';
    } catch (error) {
      console.error('Bundle submit failed:', error);
      alert(error?.message || 'Sorry, something went wrong adding your bundle.');
    } finally {
      this.setLoading(false);
      this.isSubmitting = false;
    }
  }

  buildBundleProperties(bundleId) {
    const properties = {
      '__history': '[]',
      '__bundle': 'true',
      '_bundle_id': bundleId
    };

    // Selected designs into properties
    if (Array.isArray(this.selectedProducts)) {
      this.selectedProducts.forEach((productId, index) => {
        const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
        if (!productCard) return;

        const sku = productCard.dataset.sku || '';
        const productImage = productCard.querySelector('.bundle-product-image img');
        const productTitle = productCard.querySelector('.bundle-product-info h3');

        properties[`Bundle ${index + 1}`] = productTitle ? productTitle.textContent.trim() : '';
        properties[`_bundle_image_${index + 1}`] = productImage?.src ? this.normalizeImageUrl(productImage.src) : '';
        properties[`_sku${index + 1}`] = sku;
      });

      for (let i = this.selectedProducts.length; i < this.MAX_SELECTIONS; i++) {
        properties[`Bundle ${i + 1}`] = '';
        properties[`_bundle_image_${i + 1}`] = '';
        properties[`_sku${i + 1}`] = '';
      }
    }

    // Kits / gift metadata (safe; doesn’t impact item ids)
    const kit1Title = this.getHTML('#kit-1-title');
    const kit1Img = this.getSrc('#kit-1-img');
    const kit1Id = this.getText('#kit-1-id');
    if (kit1Title) properties['Starter Kit 1'] = kit1Title;
    if (kit1Img) properties['_bundle_image_kit1'] = kit1Img;
    if (kit1Id) properties['_kit1_id'] = kit1Id;

    const kit2Title = this.getHTML('#kit-2-title');
    const kit2Img = this.getSrc('#kit-2-img');
    const kit2Id = this.getText('#kit-2-id');
    if (kit2Title) properties['Starter Kit 2'] = kit2Title;
    if (kit2Img) properties['_bundle_image_kit2'] = kit2Img;
    if (kit2Id) properties['_kit2_id'] = kit2Id;

    if (window.FREE_GIFT_VARIANT_ID) {
      const freeTitle = this.getHTML('#free-title');
      const freeImg = this.getHTML('#free-img');
      const freeSku = this.getHTML('#free-sku');
      if (freeTitle) properties['Free Gift'] = freeTitle;
      if (freeImg) properties['_free_gift_image'] = freeImg;
      if (freeSku) properties['_free_gift_sku'] = freeSku;
    }

    return properties;
  }

  buildItems(mainVariantId, quantity, bundleId, properties) {
    const items = [];

    // main paid item
    items.push({
      id: mainVariantId,
      quantity,
      properties
    });

    // selected children
    this.selectedProducts.forEach((productId) => {
      const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
      const vid = this.safeParseInt(productCard?.querySelector('input[name="id"]')?.value);
      if (!vid) return;
      items.push({
        id: vid,
        quantity: 1,
        properties: { _bundle_id: bundleId, _free: true }
      });
    });

    // kits
    const kit1VariantId = this.safeParseInt(this.getText('#kit-1-id'));
    if (kit1VariantId) {
      items.push({ id: kit1VariantId, quantity: 1, properties: { _bundle_id: bundleId, _free: true } });
    }

    const kit2VariantId = this.safeParseInt(this.getText('#kit-2-id'));
    if (kit2VariantId) {
      items.push({ id: kit2VariantId, quantity: 1, properties: { _bundle_id: bundleId, _free: true } });
    }

    // free gift
    if (window.FREE_GIFT_VARIANT_ID) {
      const freeIdFromDom = this.safeParseInt(this.getText('#free-id'));
      const freeIdFromGlobal = this.safeParseInt(window.FREE_GIFT_VARIANT_ID);
      const freeVariantId = freeIdFromDom || freeIdFromGlobal;
      if (freeVariantId) {
        items.push({ id: freeVariantId, quantity: 1, properties: { _bundle_id: bundleId, _free: true } });
      }
    }

    return items;
  }

  buildExpectedMap(mainVariantId, quantity, bundleId) {
    // variantId -> { qty, isMain }
    const expected = new Map();
    expected.set(mainVariantId, { qty: quantity, isMain: true });

    // children: qty always 1 each (but allow duplicates)
    this.selectedProducts.forEach((productId) => {
      const card = this.modal?.querySelector(`[data-product-id="${productId}"]`);
      const vid = this.safeParseInt(card?.querySelector('input[name="id"]')?.value);
      if (!vid) return;
      const cur = expected.get(vid);
      expected.set(vid, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
    });

    const kit1 = this.safeParseInt(this.getText('#kit-1-id'));
    if (kit1) {
      const cur = expected.get(kit1);
      expected.set(kit1, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
    }

    const kit2 = this.safeParseInt(this.getText('#kit-2-id'));
    if (kit2) {
      const cur = expected.get(kit2);
      expected.set(kit2, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
    }

    if (window.FREE_GIFT_VARIANT_ID) {
      const free = this.safeParseInt(this.getText('#free-id')) || this.safeParseInt(window.FREE_GIFT_VARIANT_ID);
      if (free) {
        const cur = expected.get(free);
        expected.set(free, { qty: (cur?.qty || 0) + 1, isMain: false, bundleId });
      }
    }

    return expected;
  }

  async addNewBundleProductsVerified(mainVariantId, quantity) {
    const bundleId = `bundle-${Date.now()}`;
    const properties = this.buildBundleProperties(bundleId);

    // keep hidden inputs in sync (optional)
    try {
      if (this.mainForm) {
        Object.entries(properties).forEach(([key, value]) => {
          let input = this.mainForm.querySelector(`input[name="properties[${key}]"]`);
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${key}]`;
            this.mainForm.appendChild(input);
          }
          input.value = value;
        });
      }
    } catch (e) {
      console.warn('Hidden properties sync failed (non-fatal):', e);
    }

    const items = this.buildItems(mainVariantId, quantity, bundleId, properties);

    // Add (attempt multi-add)
    let addOk = false;
    try {
      await this.shopifyPost('cart/add.js', { items });
      addOk = true;
    } catch (err) {
      console.warn('Multi-add failed, attempting sequential add:', err);

      // Sequential fallback
      await this.shopifyPost('cart/add.js', { items: [items[0]] });
      for (let i = 1; i < items.length; i++) {
        await this.shopifyPost('cart/add.js', { items: [items[i]] });
      }
      addOk = true;
    }

    if (!addOk) throw new Error('Failed to add bundle');

    // Verify & repair (single retry)
    const expected = this.buildExpectedMap(mainVariantId, quantity, bundleId);
    const cart = await this.getCart();

    const missing = [];
    for (const [vid, meta] of expected.entries()) {
      const have = meta.isMain
        ? this.countVariantInCart(cart, vid, null)
        : this.countVariantInCart(cart, vid, bundleId);

      if (have < meta.qty) {
        missing.push({ id: vid, quantity: meta.qty - have, isMain: meta.isMain });
      }
    }

    if (!missing.length) return;

    console.warn('Detected missing bundle lines; repairing:', missing);

    const repairItems = missing.map((m) => ({
      id: m.id,
      quantity: m.quantity,
      properties: m.isMain ? properties : { _bundle_id: bundleId, _free: true }
    }));

    await this.shopifyPost('cart/add.js', { items: repairItems });

    // Re-check once
    const cart2 = await this.getCart();
    const stillMissing = [];
    for (const [vid, meta] of expected.entries()) {
      const have = meta.isMain
        ? this.countVariantInCart(cart2, vid, null)
        : this.countVariantInCart(cart2, vid, bundleId);
      if (have < meta.qty) stillMissing.push({ id: vid, want: meta.qty, have });
    }

    if (stillMissing.length) {
      console.error('Still missing after repair:', stillMissing);
      throw new Error('Some bundle items could not be confirmed in the cart. Please try again.');
    }
  }

  // ---------------------------
  // Modal UI
  // ---------------------------
  open() {
    if (!this.modal) return;
    this.modal.hidden = false;
    this.modal.classList.add('bundle-product-modal--open');
    document.body.style.overflow = 'hidden';
    document.body.style.paddingRight = '15px';
    this.overlay?.classList.remove('hidden');
    this.restoreSelectedState();

    const slots = this.modal.querySelectorAll('.swiper-wrapper .bundle-product-slot');
    slots.forEach((slot, index) => {
      const title = slot.querySelector('.bundle-slot-title');
      if (title) title.textContent = `Design ${index + 1}`;
    });
  }

  close() {
    if (!this.modal) return;
    this.modal.hidden = true;
    this.modal.classList.remove('bundle-product-modal--open');
    document.body.style.overflow = 'auto';
    document.body.style.paddingRight = '0';
    this.overlay?.classList.add('hidden');
    this.updateAddToCartButton();
  }

  updateProgress() {
    const count = this.selectedProducts.length;
    const percentage = (count / this.MAX_SELECTIONS) * 100;

    const countText = `<span>${count}</span> of ${this.MAX_SELECTIONS} selected`;
    if (this.mainCountTextElement) this.mainCountTextElement.innerHTML = countText;
    if (this.modalCountTextElement) this.modalCountTextElement.innerHTML = countText;

    if (this.progress) this.progress.style.width = `${percentage}%`;
    if (this.formProgress) this.formProgress.style.width = `${percentage}%`;
  }

  updateCounter() {
    const count = this.selectedProducts.length;
    if (this.selectedCounter) this.selectedCounter.textContent = String(count);
  }

  findNextEmptySlot() {
    for (let i = 0; i < this.MAX_SELECTIONS; i++) {
      if (!this.slotSelections[i + 1]) return i + 1;
    }
    return null;
  }

  toggleProduct(button) {
    const productId = button.dataset.productId;
    const productCard = button.closest('.bundle-product-card');

    // prevent duplicates
    if (this.selectedProducts.includes(productId)) return;

    this.currentSlot = this.findNextEmptySlot();
    if (!this.currentSlot) return;

    this.selectedProducts.push(productId);

    // HHP Customisation START
    this.trackedProductIds.push(productId);
    // HHP Customisation END

    this.slotSelections[this.currentSlot] = productId;
    productCard?.classList.add('selected');

    this.updateSelectedSlot(productId);

    this.currentSlot = this.findNextEmptySlot();
    this.updateProgress();
    this.updateCounter();
    this.updateButtonState();

    if (this.selectedProducts.length === this.MAX_SELECTIONS) {
      const realButton = document.querySelector('.bundle-product-toggle-real');
      const fakeButton = document.querySelector('.bundle-product-toggle-fake');
      if (realButton && fakeButton) {
        realButton.style.display = 'block';
        fakeButton.style.display = 'none';
      }
      this.close();
    }
  }

  updateButtonState() {
    const realButton = document.querySelector('.bundle-product-toggle-real');
    const fakeButton = document.querySelector('.bundle-product-toggle-fake');
    const selectedCount = this.selectedProducts.length;

    if (selectedCount === this.MAX_SELECTIONS) {
      if (realButton) {
        realButton.style.display = 'block';
        realButton.disabled = false;
      }
      if (fakeButton) fakeButton.style.display = 'none';
    } else {
      if (realButton) realButton.style.display = 'none';
      if (fakeButton) {
        fakeButton.style.display = 'block';
        const textElement = fakeButton.querySelector('[data-add-to-bundle-text]');
        if (textElement) textElement.textContent = `Choose your Design`;
      }
    }
  }

  updateMaxSelections() {
    const selectedOption = document.querySelector('[name="options[Option]"]:checked');
    const oldMax = this.MAX_SELECTIONS;
    if (!selectedOption) return;

    const variantTitle = selectedOption.value;
    const variantOption = document.querySelector(
      `select[name="id"] option[value="${selectedOption.closest('input')?.value}"]`
    );

    if (variantOption && variantOption.dataset.bundleMaxItems) {
      this.MAX_SELECTIONS = parseInt(variantOption.dataset.bundleMaxItems, 10) || 4;
    } else {
      switch (variantTitle) {
        case 'SAVE 30%':
          this.MAX_SELECTIONS = 1;
          break;
        case 'SAVE 35%':
          this.MAX_SELECTIONS = 2;
          break;
        case 'SAVE 40%':
          this.MAX_SELECTIONS = 3;
          break;
        default:
          this.MAX_SELECTIONS = 3;
      }
    }

    this.updateSlotVisibility();
    this.updateCounter();

    if (oldMax !== this.MAX_SELECTIONS) {
      this.updateProgress();
      this.updateButtonState();

      const swiperContainer = document.querySelector('.bundle-product-toggle-buttons .swiper');
      if (this.bundle_slide) {
        this.bundle_slide.destroy(true, true);
        this.bundle_slide = null;
      }

      if (this.MAX_SELECTIONS === 2) {
        document.querySelector('.swatch-button-animation').style.left = '33%';
        document.querySelector('.swatch-button-animation').innerHTML = 'SAVE 35%';
        document.querySelector('.bundle-product-toggle-text strong').innerHTML = 'Choose <br>your designs';
        document.querySelector('.bundle-product-modal .bundle-product-toggle-text strong').innerHTML = 'Choose your designs';
        document.querySelector('.bundle-product-toggle-grid').style.gridTemplateColumns = 'repeat(3, 1fr)';
      } else if (this.MAX_SELECTIONS === 3) {
        document.querySelector('.swatch-button-animation').style.left = '67%';
        document.querySelector('.swatch-button-animation').innerHTML = 'SAVE 40%';
        document.querySelector('.bundle-product-toggle-text strong').innerHTML = 'Choose <br>your designs';
        document.querySelector('.bundle-product-modal .bundle-product-toggle-text strong').innerHTML = 'Choose your designs';
        document.querySelector('.bundle-product-toggle-grid').style.gridTemplateColumns = 'repeat(3, 1fr)';
      } else {
        swiperContainer?.closest('.bundle-product-toggle-buttons')?.classList.add('grid-4-items');
        document.querySelector('.swatch-button-animation').innerHTML = 'SAVE 30%';
        document.querySelector('.swatch-button-animation').style.left = '0';
        document.querySelector('.bundle-product-toggle-text strong').innerHTML = 'Choose <br>your design';
        document.querySelector('.bundle-product-modal .bundle-product-toggle-text strong').innerHTML = 'Choose your design';
        document.querySelector('.bundle-product-toggle-grid').style.gridTemplateColumns = 'repeat(3, 1fr)';
      }
    }
  }

  updateSlotVisibility() {
    ['', '.bundle-modal-header '].forEach((selector) => {
      const slots = document.querySelectorAll(`${selector}.bundle-product-slot`);
      slots.forEach((slot, index) => {
        if (index < this.MAX_SELECTIONS) {
          slot.classList.remove('hidden');
          const badge = slot.querySelector('.bundle-slot-badge');
          if (badge && index === 3) {
            if (this.MAX_SELECTIONS === 6) badge.classList.add('hidden');
            else badge.classList.remove('hidden');
          }
        } else {
          slot.classList.add('hidden');
        }
      });
    });
  }

  updateSelectedSlot(productId) {
    const slot = this.bundleSlots[this.currentSlot - 1];
    if (!slot) return;

    const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
    if (!productCard) return;

    const productImage = productCard.querySelector('.bundle-product-image img');

    const apply = (targetSlot) => {
      const targetContent = targetSlot.querySelector('.bundle-slot-content');
      const targetPlaceholder = targetSlot.querySelector('.bundle-slot-placeholder');
      const targetImage = targetContent?.querySelector('img');
      const targetTitle = targetSlot.querySelector('.bundle-slot-title');

      if (!targetImage || !productImage?.src) return;

      targetImage.src = productImage.src;
      targetImage.onload = () => {
        targetContent.classList.remove('hidden');
        targetPlaceholder.classList.add('hidden');
        targetSlot.classList.add('selected');
        if (targetTitle) targetTitle.textContent = productImage.alt || `Design ${this.currentSlot}`;
      };
    };

    apply(slot);

    const headerSlot = this.modal?.querySelector(
      `.bundle-modal-header .bundle-product-slot[data-slot="${this.currentSlot}"]`
    );
    if (headerSlot) apply(headerSlot);
  }

  clearSlot(slotNumber) {
    const slot = this.bundleSlots[slotNumber - 1];
    if (!slot) return;

    const content = slot.querySelector('.bundle-slot-content');
    const placeholder = slot.querySelector('.bundle-slot-placeholder');
    const image = content?.querySelector('img');
    const title = slot.querySelector('.bundle-slot-title');

    if (image) image.src = '';

    content?.classList.add('hidden');
    placeholder?.classList.remove('hidden');
    slot.classList.remove('selected');
    if (title) title.textContent = `Design ${slotNumber}`;

    const headerSlot = this.modal?.querySelector(`.bundle-modal-header .bundle-product-slot[data-slot="${slotNumber}"]`);
    if (headerSlot) {
      const headerContent = headerSlot.querySelector('.bundle-slot-content');
      const headerPlaceholder = headerSlot.querySelector('.bundle-slot-placeholder');
      const headerImage = headerContent?.querySelector('img');
      const headerTitle = headerSlot.querySelector('.bundle-slot-title');

      if (headerImage) headerImage.src = '';
      headerContent?.classList.add('hidden');
      headerPlaceholder?.classList.remove('hidden');
      headerSlot.classList.remove('selected');
      if (headerTitle) headerTitle.textContent = `Design ${slotNumber}`;
    }

    delete this.slotSelections[slotNumber];
  }

  resetAllSelections() {
    this.selectedProducts = [];
    this.slotSelections = {};
    this.trackedProductIds = [];

    this.bundleSlots.forEach((slot, index) => {
      this.clearSlot(index + 1);
      slot.classList.remove('selected');
    });

    this.modal?.querySelectorAll('.bundle-product-card.selected').forEach((card) => {
      card.classList.remove('selected');
    });

    const realButton = document.querySelector('.bundle-product-toggle-real');
    const fakeButton = document.querySelector('.bundle-product-toggle-fake');

    if (realButton && fakeButton) {
      realButton.style.display = 'none';
      fakeButton.style.display = 'block';
      const el = fakeButton.querySelector('[data-add-to-bundle-text]');
      if (el) el.textContent = 'Choose your colours first';
    }

    this.updateCounter();
    this.updateProgress();
    this.updateButtonState();
  }

  removeSelection(slotNumber) {
    const productId = this.slotSelections[slotNumber];
    if (!productId) return;

    const index = this.selectedProducts.indexOf(productId);
    if (index > -1) this.selectedProducts.splice(index, 1);

    const trackedIndex = this.trackedProductIds.indexOf(productId);
    if (trackedIndex > -1) this.trackedProductIds.splice(trackedIndex, 1);

    this.clearSlot(slotNumber);

    const productCard = this.modal?.querySelector(`[data-product-id="${productId}"]`);
    if (productCard) productCard.classList.remove('selected');

    this.currentSlot = slotNumber;

    this.updateProgress();
    this.updateCounter();
    this.updateButtonState();
    this.updateAddToCartButton();
  }

  // Collection switching
  switchCollection(collectionId) {
    this.modal?.querySelectorAll('.bundle-products-grid').forEach((grid) => grid.classList.add('hidden'));

    const selectedGrid = this.modal?.querySelector(`.bundle-products-grid[data-collection-id="${collectionId}"]`);
    if (selectedGrid) selectedGrid.classList.remove('hidden');

    this.modal?.querySelectorAll('.bundle-collections a').forEach((link) => {
      link.classList.toggle('active', link.dataset.collectionId === collectionId);
    });
  }

  initializeFirstCollection() {
    const firstCollectionLink = this.modal?.querySelector('.bundle-collections a');
    if (firstCollectionLink) this.switchCollection(firstCollectionLink.dataset.collectionId);
  }
}

// Global helper (kept for compatibility)
function openBundleModal(slotNumber) {
  if (window.bundleModal) {
    window.bundleModal.currentSlot = slotNumber;
    window.bundleModal.open();
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.bundleModal = new BundleModal();
});

// Extra swiper (your original)
const bundle_collections_slide = new Swiper('.bundle-collections .swiper', {
  slidesPerView: 'auto',
  spaceBetween: 8,
  slidesOffsetBefore: 15,
  slidesOffsetAfter: 15,
  breakpoints: {
    768: {
      slidesOffsetBefore: 20,
      slidesOffsetAfter: 20
    }
  }
});

// Inventory helpers (left in place; you said availability is guaranteed)
function checkInventory(variantId, quantity) {
  const productCard = document.querySelector(`[data-variant-id="${variantId}"]`);
  if (!productCard) return true;
  const inventoryQuantity = parseInt(productCard.dataset.inventoryQuantity, 10);
  if (isNaN(inventoryQuantity)) return true;
  return inventoryQuantity >= quantity;
}
